import type { WorkflowStatus } from './index';

/**
 * TypeScript Types for Drafting Workflow
 *
 * Surviving types after Plan 184 (#783) cleanup. Removed: refinement +
 * revert request/response types, template listing/detail types, and the
 * Word-export response wrapper. Everything kept here is consumed by the
 * Desk-native drafting flow (DraftingConfigModal, DraftingTabView).
 */

// ==============================================================================
// Enums for Drafting Workflow
// ==============================================================================

/**
 * Available contract template types
 * Matches backend TemplateType enum
 */
export enum TemplateType {
  MSA = 'msa',
  SAAS = 'saas',
  NDA_MUTUAL = 'nda_mutual',
  NDA_ONEWAY = 'nda_oneway',
  PROFESSIONAL_SERVICES = 'professional_services',
  SOFTWARE_LICENSE = 'software_license',
  SOW = 'sow',
  DPA = 'dpa',
}

/**
 * Drafting stance - whose interests to favour
 * Matches backend DraftingStance enum
 */
export enum DraftingStance {
  CUSTOMER = 'customer',
  VENDOR = 'vendor',
  BALANCED = 'balanced',
}

/**
 * Document complexity level
 * Matches backend DraftingComplexity enum
 */
export enum DraftingComplexity {
  SIMPLE = 'simple',      // <10 pages
  MODERATE = 'moderate',  // 10-30 pages
  COMPLEX = 'complex',    // 30+ pages
}

/**
 * Contract structure type
 * Matches backend StructureType enum
 */
export enum StructureType {
  MAIN_ONLY = 'main_only',
  WITH_COMMERCIAL = 'with_commercial',
  WITH_SCHEDULES = 'with_schedules',
}

/**
 * Available schedule types for contract attachments
 */
export enum Schedule {
  SECURITY = 'security',
  GOVERNANCE = 'governance',
  SERVICE_MANAGEMENT = 'service_management',
  CONTRACT_CHANGES = 'contract_changes',
  EXIT_TRANSITION = 'exit_transition',
  SLA = 'sla',
  POLICIES = 'policies',
  DISASTER_RECOVERY = 'disaster_recovery',
  FINANCIAL_OBLIGATIONS = 'financial_obligations',
  VALUE_ASSURANCE = 'value_assurance',
  RATE_CARDS = 'rate_cards',
}

// ==============================================================================
// Party Information Types
// ==============================================================================

export interface PartyInfo {
  name: string;
  short_name?: string;
  address?: string;
  company_number?: string;
}

// ==============================================================================
// Basic Issues (Contract Details)
// ==============================================================================

export interface BasicIssues {
  title?: string;
  commencement_date?: string;
  jurisdiction?: string;
  party1?: PartyInfo;
  party2?: PartyInfo;
  third_parties?: string;
  term_start?: string;
  term_end?: string;
  term_duration_months?: number;
  auto_renewal?: boolean;
  renewal_period?: string;
  max_renewals?: number;
  dispute_resolution?: 'litigation' | 'mediation_then_litigation' | 'arbitration';
  /** Plan 253, SoW only: the master agreement this Statement of Work sits under. */
  master_agreement?: { title?: string; date?: string };
}

// ==============================================================================
// Key Issues (Topic Selections)
// ==============================================================================

export interface KeyIssueSelection {
  selected: boolean;
  [key: string]: boolean | string | number | undefined;
}

export interface KeyIssues {
  payment?: KeyIssueSelection;
  termination?: KeyIssueSelection;
  liability?: KeyIssueSelection;
  ip?: KeyIssueSelection;
  confidentiality?: KeyIssueSelection;
  data_protection?: KeyIssueSelection;
  warranties?: KeyIssueSelection;
  indemnification?: KeyIssueSelection;
}

// ==============================================================================
// Draft Clause Interface
// ==============================================================================

/**
 * Individual draft clause. Matches backend DraftClauseResponse.
 */
export interface DraftClause {
  id: string;
  drafting_project_id: string;
  clause_type: string;
  section_order: number;
  title: string;
  content: string;
  version: number;
  parent_version_id?: string;
  created_at: string;
}

// ==============================================================================
// Drafting Project Interface
// ==============================================================================

/**
 * Full drafting project response from API
 * Matches backend DraftingProjectResponse
 */
/**
 * Plan 255: the served draft as a matter document. Absent when there is
 * nothing to show and nothing to wait for.
 */
export type DraftOutputState =
  | 'not_applicable'
  | 'saving'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'removed';

export interface DraftOutputDocument {
  state: DraftOutputState;
  document_id?: string | null;
  filename?: string | null;
  retry_available: boolean;
}

export interface DraftingProject {
  id: string;
  /** Plan 183: canonical parent analysis_run id (formerly aliased as `project_id`). */
  analysis_run_id: string;
  name?: string;
  template_type: TemplateType | string;
  stance?: DraftingStance | string;
  complexity?: DraftingComplexity | string;
  context?: string;
  industry?: string;
  product_service?: string;
  structure_type?: StructureType | string;
  schedules?: (Schedule | string)[];
  basic_issues?: BasicIssues;
  key_issues?: KeyIssues;
  special_instructions?: string;
  created_at: string;
  updated_at: string;
  clauses: DraftClause[];
  /** Plan 255: absent from an older backend, and whenever there is nothing to show. */
  output_document?: DraftOutputDocument | null;
}

// ==============================================================================
// Drafting Project Config (Form State)
// ==============================================================================

/**
 * Configuration data for creating a drafting project (form state).
 */
export interface DraftingProjectConfig {
  name: string;
  template_type: TemplateType | string;
  /** Plan 183 Phase 1: matter scoping for in-Desk drafting. */
  matter_id?: string | null;
  stance?: DraftingStance | string;
  complexity?: DraftingComplexity | string;
  context?: string;
  industry?: string;
  product_service?: string;
  structure_type?: StructureType | string;
  schedules?: (Schedule | string)[];
  basic_issues?: BasicIssues;
  key_issues?: KeyIssues;
  special_instructions?: string;
}

// ==============================================================================
// Draft Generation API Types
// ==============================================================================

export interface GenerationResponse {
  drafting_project_id: string;
  execution_id: string;
  status: 'started' | 'already_running';
  message: string;
}

export interface GenerationStatus {
  drafting_project_id: string;
  execution_id: string;
  /**
   * Plan 243 Task 6.1 (#990): widened to WorkflowStatus. The narrow union could
   * not represent `finalizing`, `started` or `cancelled`, so a generation in any
   * of them fell through every branch that matched on it.
   */
  status: WorkflowStatus;
  progress: number;
  total_clauses?: number;
  completed_clauses?: number;
  current_clause?: string;
  message?: string;
  error?: string;
  created_at?: string;
  completed_at?: string;
}

// ==============================================================================
// Base Document RAG Types
// ==============================================================================

export interface BaseDocumentUploadResponse {
  document_id: string;
  drafting_project_id: string;
  filename: string;
  file_size: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  message: string;
}

export interface BaseDocumentStatus {
  drafting_project_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  document_id?: string;
  filename?: string;
  chunk_count: number;
  total_tokens?: number;
  processing_cost?: number;
  error_message?: string;
  created_at?: string;
  completed_at?: string;
}

// ==============================================================================
// Plan 185: Multi-precedent types
// ==============================================================================

export type PrecedentSourceType = 'uploaded' | 'workbench';
export type PrecedentRole = 'primary' | 'supporting' | 'pending_primary';
export type PrecedentStatus = 'processing' | 'ready' | 'failed';

export interface PrecedentResponse {
  precedent_id: string;
  drafting_project_id: string;
  source_type: PrecedentSourceType;
  source_id: string;
  role: PrecedentRole;
  status: PrecedentStatus;
  ordering: number;
  display_name: string;
  chunk_count: number;
}

export interface PrecedentListResponse {
  drafting_project_id: string;
  precedents: PrecedentResponse[];
}

export interface PrecedentStatusResponse {
  precedent_id: string;
  status: PrecedentStatus;
  chunk_count: number;
  role: PrecedentRole;
}

export interface ReplacePrimaryResponse {
  precedent_id: string;
  status: PrecedentStatus;
  mode: 'swapped' | 'pending_replacement';
}

// Local-only staged shape (modal state before submit). Either `file` (upload)
// or `workbench_document_id` (workbench) is set. Once the precedent is
// posted to the server, `attached` carries the persistent id + last-known
// status so retries can skip and polling can resume.
export type StagedPrecedentRole = 'primary' | 'supporting';

export interface StagedPrecedentWorkbench {
  role: StagedPrecedentRole;
  source: 'workbench';
  workbench_document_id: string;
  display_name: string;
  attached?: { precedent_id: string; status: PrecedentStatus };
}

export interface StagedPrecedentUpload {
  role: StagedPrecedentRole;
  source: 'uploaded';
  file: File;
  display_name: string;
  attached?: { precedent_id: string; status: PrecedentStatus };
}

export type StagedPrecedent = StagedPrecedentWorkbench | StagedPrecedentUpload;
